import { memo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { Box, Checkbox, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { IconMap } from "../questionTypes";
import { deleteQuestion } from "../../../Redux/actions/questionAction";
import {
  setQuestionModalDetails,
  setQuestionSubmitting,
} from "../../../Redux/reducers/questionSlice";
import { KeyboardArrowUp, KeyboardArrowDown } from "@mui/icons-material";
import { copyQuestionMiddleware } from "../QuestionProperties/utils";
import DeleteConfirmModal from "./DeleteConfirm";
import { errorToastMessage } from "../../../utils/toast";
import http from "../../../utils/http";
import MDEditor from "@uiw/react-md-editor/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import { PreviewcomponentMap } from "../QuestionPreview/PreviewMap";
import {
  QuestionItemStyle,
  QuestionDescriptorStyle,
  requiredStyles,
} from "../question.style";
import { CopyOutlinedIcon, DeleteIcon, EditIcon } from "../Icons";

type Props = {
  question: any;
  index: number;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
};

const QuestionItemWrapper: React.FC<Props> = ({
  question,
  index,
  selected = false,
  onToggleSelect,
}) => {
  const dispatch = useAppDispatch();

  const formId = useAppSelector((state) => state.question.formId);
  const studyId = useAppSelector((state) => state.question.studyId);
  const editable = useAppSelector((state) => state.question.editable);
  const formLevel = useAppSelector((state) => state.question.formLevel);
  const formSubmitting = useAppSelector(
    (state) => state.question.formSubmitting
  );

  const Icon = IconMap[question.type];

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showChoices, setShowChoices] = useState(false);
  const [data, setData] = useState<any[]>([]);

  const handleCopyQuestion = () => {
    const modifiedQuestion = copyQuestionMiddleware(question);
    dispatch(
      setQuestionModalDetails({
        question: modifiedQuestion,
        show: true,
        modalIsEdit: false,
      })
    );
  };

  const handleEditQuestion = () => {
    dispatch(
      setQuestionModalDetails({
        question,
        show: true,
        modalIsEdit: true,
      })
    );
  };

  const handleDeleteQuestion = () => {
    dispatch(deleteQuestion(question.id));
  };

  const handleDeleteQuestionPopup = async () => {
    if (!formSubmitting) {
      dispatch(setQuestionSubmitting(true));
      const prefix =
        formLevel === "platform" ? "/admin/registry/" : `/study/${studyId}/`;

      try {
        const { data } = await http.get(
          `${prefix}forms/${formId}/question/${question.id}/dependencies`
        );
        const resData = data?.data;
        setData(resData);
        dispatch(setQuestionSubmitting(false));
        setShowDeleteModal(true);
      } catch (error) {
        dispatch(setQuestionSubmitting(false));
        errorToastMessage(error as Error);
      }
    }
  };

  const Component = PreviewcomponentMap[question.type];

  return (
    <Stack sx={QuestionItemStyle} justifyContent={"center"} gap={2}>
      <Stack
        direction="row"
        alignItems={"flex-start"}
        justifyContent={"space-between"}
      >
        <Stack direction="row" alignItems={"flex-start"}>
          {editable && onToggleSelect && (
            <Checkbox
              checked={selected}
              onChange={(e) => {
                e.stopPropagation();
                onToggleSelect(question.id);
              }}
              onClick={(e) => e.stopPropagation()}
              size="small"
              sx={{ p: "4px", mr: 0.5, alignSelf: "flex-start", mt: "2px" }}
            />
          )}
          <Stack
            sx={QuestionDescriptorStyle}
            direction="row"
            alignItems={"center"}
            justifyContent={"space-between"}
            flexShrink={0}
          >
            <Typography fontWeight={"regular"} variant="subtitle1">
              {question?.properties?.displayNumber
                ? question?.properties?.displayNumber
                : index + 1}
            </Typography>
            {Icon && <Icon />}
          </Stack>

          <Box sx={{ flex: 1, minWidth: "0px" }}>
            {/* <Typography
              fontSize={16}
              fontWeight={500}
              sx={question?.properties?.required ? requiredStyles : {}}
            >
              {question.label}
            </Typography> */}
            <Box sx={question?.properties?.required ? requiredStyles : {}}>
              <MDEditor.Markdown
                wrapperElement={{
                  "data-color-mode": "light",
                }}
                source={question.labelMarkdown}
                rehypePlugins={[rehypeSanitize]}
                style={{ fontWeight: "600", fontSize: "14px" }}
              />
            </Box>

            <Typography
              variant="body2"
              fontWeight={"regular"}
              sx={{ color: "text.secondary" }}
              mt={1}
            >
              ({question.varname})
              {question?.dependency && (
                <DependencyTextPreview dep={question?.dependency} />
              )}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems={"center"}>
          {![
            "statement",
            "randomize",
            "summary",
            "eligibility",
            "repeated_measure",
          ].includes(question.type) && (
            <Tooltip title={showChoices ? "Hide Preview" : "Show Preview"}>
              <IconButton onClick={() => setShowChoices((prev) => !prev)}>
                {showChoices ? (
                  <KeyboardArrowUp sx={{ color: "text.secondary" }} />
                ) : (
                  <KeyboardArrowDown sx={{ color: "text.secondary" }} />
                )}
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title={editable ? "Edit" : "View Details"}>
            <IconButton
              onClick={handleEditQuestion}
              sx={{ height: 40, width: 40 }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>

          {editable && (
            <Tooltip title={"Duplicate"}>
              <IconButton
                onClick={handleCopyQuestion}
                sx={{ height: 40, width: 40 }}
              >
                <CopyOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}

          {editable && (
            <Tooltip title={"Delete"}>
              <IconButton
                onClick={handleDeleteQuestionPopup}
                disabled={formSubmitting}
                sx={{ height: 40, width: 40 }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          )}
        </Stack>

        {editable && showDeleteModal && (
          <DeleteConfirmModal
            title={question.varname}
            onOk={handleDeleteQuestion}
            show={showDeleteModal}
            data={data}
            onClose={() => setShowDeleteModal(false)}
          />
        )}
      </Stack>

      {Component && showChoices && (
        <Stack gap={2} sx={{ width: "85%", pl: "92px" }}>
          {question?.signedImageUrl && (
            <Box sx={{ maxHeight: 97, maxWidth: 192, borderRadius: "8px" }}>
              <img
                src={question.signedImageUrl}
                alt="preview"
                style={{
                  maxHeight: "97px",
                  maxWidth: "192px",
                  objectFit: "contain",
                  borderRadius: "8px",
                }}
              />
            </Box>
          )}
          <Component currentQuestion={question} />
        </Stack>
      )}
    </Stack>
  );
};

export default memo(QuestionItemWrapper);

const symbolMap: Record<string, string> = {
  eq: "is equal to",
  not_eq: "is not",
  gt: "greater than",
  lt: "less than",
  gte: "greater than or equal to",
  lte: "less than or equal to",
  is_checked: "is checked",
  is_not_checked: "is not checked",
};

const keyMap: Record<string, string> = {
  text: "textValue",
  date: "textValue",
  number: "numberValue",
  slider: "numberValue",
  calculated_field: "numberValue",
  calculated_field_text: "textValue",
  radio: "questionChoice",
  dropdown: "questionChoice",
  checkbox: "questionChoice",
  randomize: "textValue",
};

const DependencyTextPreview = ({ dep }: { dep: any }) => {
  const dependent = dep?.parentQuestion?.varname;
  const operator = symbolMap[dep?.operator];
  let type = dep?.parentQuestion?.type;
  if (
    type === "calculated_field" &&
    dep?.parentQuestion?.properties?.responseType === "string"
  ) {
    type = "calculated_field_text";
  }
  const label = ["radio", "dropdown", "checkbox"].includes(type)
    ? dep?.[keyMap[type]]?.label
    : dep?.[keyMap[type]];

  return (
    <>
      {type === "checkbox" ? (
        <Typography
          variant="body2"
          fontWeight={"regular"}
          sx={{ color: "text.secondary" }}
          component={"span"}
        >
          &nbsp;&nbsp;|&nbsp;&nbsp; ({label} in {dependent}
          &nbsp;
          <strong>{operator}</strong>)
        </Typography>
      ) : (
        <Typography
          variant="body2"
          fontWeight={"regular"}
          sx={{ color: "text.secondary" }}
          component={"span"}
        >
          &nbsp;&nbsp;|&nbsp;&nbsp; ({dependent} <strong>{operator}</strong>{" "}
          {label})
        </Typography>
      )}
    </>
  );
};
